<template>
  <or-popover
    data-component="base-search-options-popup"
    placement="bottom-start"
    :trigger="trigger"
    :is-open="isPopupOpen"
    @close="$emit('close')"
  >
    <div
      v-for="option of optionsBasedOnActiveQueryParam"
      :key="option.key"
      :data-option="option.key"
      @click.stop="onOptionClick(option)"
    >
      {{ $t(option.label) }}
    </div>
  </or-popover>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue-demi';
import { OrPopoverV3 as OrPopover } from '@onereach/ui-components-25';

import { DateValueOption, QueryParam, SearchOption } from '../../types';
import { useSearch } from '../../composables';
import {
  excludeParamsFromSchema,
  getOptionsFromSchemaForIncompleteParam,
} from '../../common';
import {
  QUERY_PROP,
  SCHEMA_PROP,
  QUERY_PARAM_TYPE,
  PATH_DELIMITER,
} from '../../constants';

/** Do not propagate click events to keep Search Input in focus */
export default defineComponent({
  name: 'BaseSearchOptionsPopup',
  components: { OrPopover },
  props: {
    trigger: {
      type: Object as PropType<HTMLElement>,
      default: () => ({}),
    },
    query: QUERY_PROP,
    schema: SCHEMA_PROP,
    isOpen: Boolean,
  },
  emits: ['close'],
  setup(props) {
    const { schemaAsMap, activeQueryParam, isActiveQueryParamComplete } = useSearch(props);
    return { schemaAsMap, activeQueryParam, isActiveQueryParamComplete };
  },
  computed: {
    isPopupOpen() {
      return this.isOpen && !!this.optionsBasedOnActiveQueryParam.length;
    },

    /**
     * Root schema items, object and date range schemas can display list of options
     * Already included params should not be present in available options
     */
    optionsBasedOnActiveQueryParam() {
      const options = this.checkAndReturnSimpleOptions();
      if (options) {
        return options;
      }

      const query = this.isActiveQueryParamComplete
        ? this.query
        : this.query.slice(0, -1);

      const schema = excludeParamsFromSchema({ query, schema: this.schemaAsMap });
      if (this.isActiveQueryParamComplete) {
        return getOptionsFromSchemaForIncompleteParam({ schema });
      }

      return getOptionsFromSchemaForIncompleteParam({
        param: this.activeQueryParam,
        schema,
      });
    },
  },
  methods: {
    onOptionClick(option: SearchOption) {
      const query = this.isActiveQueryParamComplete
        ? this.query
        : this.query.slice(0, -1);

      let param = this.checkAndApplyDateValueOption(option);
      if (!param) {
        param = this.applyGenericOption(option);
      }

      query.push(param);
      this.$emit('query-change', query);
    },

    applyGenericOption(option: SearchOption) {
      const path = this.isActiveQueryParamComplete
        ? option.key
        : this.activeQueryParam!.key + PATH_DELIMITER + option.key;

      return { key: path } as QueryParam;
    },

    /** Date value option should have a full or at least partially complete value */
    checkAndApplyDateValueOption(option: SearchOption) {
      const getValue = (option as DateValueOption).value;
      if (!getValue) {
        return;
      }

      const param = { ...this.activeQueryParam!, value: getValue() };
      return param as QueryParam;
    },

    /** Some options can be determined without excluding existing query params first */
    checkAndReturnSimpleOptions() {
      if (!this.activeQueryParam || this.isActiveQueryParamComplete) {
        return;
      }

      const schemaItem = this.schemaAsMap[this.activeQueryParam.key];
      const isParamString = schemaItem.type === QUERY_PARAM_TYPE.STRING;
      const isParamDateRange = schemaItem.type === QUERY_PARAM_TYPE.DATE_RANGE;
      const hasNonEmptyValue = !!this.activeQueryParam.value;

      if (isParamString || (isParamDateRange && hasNonEmptyValue)) {
        return [] as SearchOption[];
      }

      if (isParamDateRange && !hasNonEmptyValue) {
        return schemaItem.options || [];
      }

      return;
    },
  },
});
</script>

<style lang="scss">
@use '@/styles' as *;

/* TODO: Styles are not scoped due to mobile OrPopover version using portal! */
[data-component='base-search-options-popup'] {
  min-width: 10rem;
}

/* TODO: Mobile OrPopover doesn't apply data-component attribute! */
[data-option] {
  line-height: 1.5;
  font-size: var(--fs-0);
  color: var(--or-c-on-background);
  padding: var(--s-3);
  cursor: pointer;

  @include max-md {
    padding: 0.75rem var(--s-5);
    font-size: 1rem;
  }

  &:hover {
    background: var(--or-c-primary-opacity-0-08);
  }
}
</style>
